<template>
  <v-card
    v-if="control.visible"
    :class="styles.arrayList.root"
    elevation="0"
    v-bind="vuetifyProps('v-card')"
  >
    <v-card-title v-bind="vuetifyProps('v-card-title')">
      <v-toolbar
        flat
        :class="styles.arrayList.toolbar"
        v-bind="vuetifyProps('v-toolbar')"
      >
        <v-toolbar-title
          :class="styles.arrayList.label"
          v-bind="vuetifyProps('v-toolbar-title')"
          >{{ computedLabel }}</v-toolbar-title
        >
        <validation-icon
          v-if="control.childErrors.length > 0"
          :errors="control.childErrors"
        />
        <v-spacer></v-spacer>

        <v-tooltip bottom>
          <template v-slot:activator="{ on: onTooltip }">
            <v-btn
              fab
              text
              elevation="0"
              small
              :aria-label="`Add to ${control.label}`"
              v-on="onTooltip"
              :class="styles.arrayList.addButton"
              :disabled="
                !control.enabled ||
                (appliedOptions.restrict &&
                  arraySchema !== undefined &&
                  arraySchema.maxItems !== undefined &&
                  dataLength >= arraySchema.maxItems)
              "
              @click="addButtonClick"
            >
              <v-icon class="notranslate">mdi-plus</v-icon>
            </v-btn>
          </template>
          {{ `Add to ${control.label}` }}
        </v-tooltip>
        <v-tooltip bottom>
          <template v-slot:activator="{ on: onTooltip }">
            <v-btn
              fab
              text
              elevation="0"
              small
              :aria-label="`Delete from ${control.label}`"
              v-on="onTooltip"
              :class="styles.arrayList.itemDelete"
              :disabled="
                !control.enabled ||
                (appliedOptions.restrict &&
                  arraySchema !== undefined &&
                  arraySchema.minItems !== undefined &&
                  (selectedIndexes.length > 0
                    ? dataLength - selectedIndexes.length < arraySchema.minItems
                    : dataLength <= arraySchema.minItems))
              "
              @click="removeItemsClick"
            >
              <v-icon class="notranslate">mdi-delete</v-icon>
            </v-btn>
          </template>
          {{ `Delete from ${control.label}` }}
        </v-tooltip>
      </v-toolbar>
    </v-card-title>
    <v-card-text v-bind="vuetifyProps('v-card-text')">
      <ag-grid-vue
        v-bind="agGridVueProps"
        :class="
          $vuetify.theme.dark ? `ag-grid-renderer-dark` : `ag-grid-renderer`
        "
        style="width: 100%; height: 250px"
        :gridOptions="gridOptions"
        :rowData="rowData"
        :enableRtl="$vuetify.rtl"
        @grid-ready="onGridReady"
        @sort-changed="onSortChanged"
        @filter-changed="onFilterChanged"
        @row-drag-end="onRowDragEnd"
        @selection-changed="onSelectionChanged"
      >
      </ag-grid-vue>
    </v-card-text>
  </v-card>
</template>

<script lang="ts">
import {
  and,
  composePaths,
  ControlElement,
  createDefaultValue,
  hasType,
  JsonFormsRendererRegistryEntry,
  JsonSchema,
  optionIs,
  rankWith,
  Resolve,
  schemaMatches,
  uiTypeIs,
} from '@jsonforms/core';
import {
  rendererProps,
  RendererProps,
  useJsonFormsArrayControl,
} from '@jsonforms/vue2';
import {
  useVuetifyArrayControl,
  ValidationBadge,
  ValidationIcon,
} from '@jsonforms/vue2-vuetify';
import {
  ColDef,
  ColGroupDef,
  ColumnApi,
  GridApi,
  GridOptions,
  GridReadyEvent,
  RowDragEndEvent,
  SelectionChangedEvent,
} from 'ag-grid-community';

const AgGridVue = () =>
  import('ag-grid-vue').then((module) => {
    const { AgGridVue } = module;
    return AgGridVue;
  });

import cloneDeep from 'lodash/cloneDeep';
import merge from 'lodash/merge';
import startCase from 'lodash/startCase';
import uniqueId from 'lodash/uniqueId';
import find from 'lodash/find';
import {
  computed,
  defineComponent,
  reactive,
  ref,
  unref,
  UnwrapNestedRefs,
} from 'vue';
import {
  VBtn,
  VCard,
  VCardText,
  VCardTitle,
  VIcon,
  VSpacer,
  VToolbar,
  VToolbarTitle,
  VTooltip,
} from 'vuetify/lib';
import AgDispatchRenderer from './AgDispatchRenderer.vue';

const useControlAppliedOptions = <I extends { control: any }>(input: I) => {
  return computed(() =>
    merge(
      {},
      cloneDeep(input.control.value.config),
      cloneDeep(input.control.value.uischema.options)
    )
  );
};

const _getRowId = Symbol('_getRowId');

const controlRenderer = defineComponent({
  name: 'ag-grid-control-renderer',
  components: {
    AgDispatchRenderer,
    AgGridVue,
    ValidationIcon,
    ValidationBadge,
    VCard,
    VCardTitle,
    VCardText,
    VToolbar,
    VToolbarTitle,
    VTooltip,
    VIcon,
    VBtn,
    VSpacer,
  },
  props: {
    ...rendererProps<ControlElement>(),
  },
  setup(props: RendererProps<ControlElement>) {
    const input = useVuetifyArrayControl(useJsonFormsArrayControl(props));
    const appliedOptions = useControlAppliedOptions(input);

    const control = unref(input.control);

    const title = (prop: string) =>
      control.schema.properties?.[prop]?.title ?? startCase(prop);

    const controlWithoutLabel = (
      scope: string,
      options: Record<string, any>
    ): ControlElement => ({
      type: 'Control',
      scope: scope,
      label: false,
      ...options,
    });

    const resolveUiSchema = (propName: string, options: Record<string, any>) =>
      control.schema.properties
        ? controlWithoutLabel(`#/properties/${propName}`, options)
        : controlWithoutLabel('#', options);

    const columnDefs = reactive({ columns: [] as (ColDef | ColGroupDef)[] });

    const schema = control.schema;
    const agGridVueOptions = appliedOptions.value['ag-grid-vue'] || {};
    const agGridVueOptionsColumnDefs: ColDef[] =
      agGridVueOptions?.gridOptions?.columnDefs ||
      agGridVueOptions?.columnDefs ||
      [];

    if (schema.type === 'object' && typeof schema.properties === 'object') {
      const columns = Object.keys(schema.properties)
        .filter((prop) => schema.properties![prop].type !== 'array')
        .map((property) => {
          const colOptions =
            find(
              agGridVueOptionsColumnDefs,
              (d: ColDef) => d.field === property
            ) || {};

          const result: ColDef = {
            ...{ headerName: title(property), ...colOptions },
            field: property,
            autoHeight: true,
            cellRendererSelector: (params) => {
              return {
                component: 'AgDispatchRenderer',
                params: {
                  control: reactive({
                    schema: control.schema,
                    uischema: resolveUiSchema(
                      property,
                      (colOptions as any).uischema
                    ),
                    path: composePaths(control.path, `${params.rowIndex}`),
                    enabled: control.enabled,
                    renderers: control.renderers,
                    cells: control.cells,
                  }),
                },
              };
            },
          };

          return result;
        });

      if (control.enabled) {
        if (appliedOptions.value.showSortButtons) {
          columnDefs.columns.push({
            rowDrag: true,
            headerName: '',
            width: 36,
            field: 'dragBtn',
            cellStyle: { alignItems: 'center', display: 'flex' },
          });
        }
        columnDefs.columns.push({
          headerName: '',
          checkboxSelection: true,
          headerCheckboxSelection: true,
          width: 50,
          field: 'checkboxBtn',
          cellStyle: { alignItems: 'center', display: 'flex' },
        });
      }
      if (agGridVueOptionsColumnDefs.length > 0) {
        columnDefs.columns.push(
          ...(agGridVueOptionsColumnDefs.map((c) =>
            columns.find((column) => c.field === column.field)
          ) as (ColDef | ColGroupDef)[])
        );
      } else {
        columnDefs.columns.push(...columns);
      }
    } else {
      if (control.enabled) {
        if (appliedOptions.value.showSortButtons) {
          columnDefs.columns.push({
            rowDrag: true,
            headerName: '',
            width: 36,
            field: 'dragBtn',
            cellStyle: { alignItems: 'center', display: 'flex' },
          });
        }
        columnDefs.columns.push({
          headerName: '',
          checkboxSelection: true,
          headerCheckboxSelection: true,
          width: 50,
          field: 'checkboxBtn',
          cellStyle: { alignItems: 'center', display: 'flex' },
        });
      }
      const colOptions =
        find(agGridVueOptionsColumnDefs, (d: ColDef) => d.field === 'value') ||
        {};

      // primitives
      columnDefs.columns.push({
        ...colOptions,
        field: 'value',
        autoHeight: true,
        cellRendererSelector: (params) => {
          return {
            component: 'AgDispatchRenderer',
            params: {
              control: reactive({
                schema: control.schema,
                uischema: resolveUiSchema('', (colOptions as any).uischema),
                path: composePaths(control.path, `${params.rowIndex}`),
                enabled: control.enabled,
                renderers: control.renderers,
                cells: control.cells,
              }),
            },
          };
        },
      });
    }

    const gridApi = ref<GridApi | null>(null);
    const gridColumnApi = ref<ColumnApi | null>(null);
    const sortActive = false;
    const filterActive = false;

    const gridOptions: UnwrapNestedRefs<GridOptions> = reactive({
      columnDefs: columnDefs.columns as ColDef[],
      getRowId: (params) => {
        return params.data[_getRowId];
      },
      rowSelection: 'multiple',
      suppressRowClickSelection: true,
    });

    const selectedIndexes = ref([] as number[]);
    return {
      ...input,
      selectedIndexes,
      gridOptions,
      sortActive,
      filterActive,
      gridApi,
      gridColumnApi,
      columnDefs,
    };
  },
  computed: {
    agGridVueProps() {
      let props = this.appliedOptions['ag-grid-vue'] || {};

      if (props.gridOptions) {
        delete props.gridOptions;
      }
      if (props.rowData) {
        delete props.rowData;
      }
      if (props.enableRtl) {
        delete props.enableRtl;
      }

      if (props.columnDefs) {
        delete props.columnDefs;
      }
      if (props.getRowId) {
        delete props.getRowId;
      }

      return props;
    },
    rowData() {
      const data = this.control.data ?? [];
      const objectArray =
        this.control.schema.type === 'object' &&
        typeof this.control.schema.properties === 'object';

      let array: any[] = data.map((d: any, index: number) => {
        if (objectArray) {
          if (d[_getRowId] === undefined) {
            d[_getRowId] = uniqueId();
          }
        } else {
          // if simple then wrap it
          d = { value: d };
          // for simple array use the index instead of uniqueId
          d[_getRowId] = index;
        }
        return d;
      });

      return array;
    },
    arraySchema(): JsonSchema | undefined {
      return Resolve.schema(
        this.control.rootSchema,
        this.control.uischema.scope,
        this.control.rootSchema
      );
    },
    dataLength(): number {
      return this.control.data ? this.control.data.length : 0;
    },
  },
  methods: {
    addButtonClick() {
      this.addItem(
        this.control.path,
        createDefaultValue(this.control.schema)
      )();
      this.$nextTick(() => {
        //this.gridApi?.ensureIndexVisible(this.dataLength - 1, 'top');
      });
    },
    removeItemsClick(): void {
      const toDelete = this.selectedIndexes;
      if (toDelete && toDelete.length > 0) {
        this.removeItems?.(this.control.path, toDelete)();
        this.$nextTick(() => {
          this.gridApi?.deselectAll();
          this.gridApi?.redrawRows();
        });
      }
    },
    onSortChanged() {
      const colState = this.gridColumnApi?.getColumnState() || [];
      this.sortActive = colState.some((c) => c.sort);
      // suppress row drag if either sort or filter is active
      const suppressRowDrag = this.sortActive || this.filterActive;
      this.gridApi?.setSuppressRowDrag(suppressRowDrag);
    },
    // listen for changes on filter changed
    onFilterChanged() {
      this.filterActive = this.gridApi?.isAnyFilterPresent() || false;
      // suppress row drag if either sort or filter is active
      const suppressRowDrag = this.sortActive || this.filterActive;
      this.gridApi?.setSuppressRowDrag(suppressRowDrag);
    },
    onRowDragEnd(event: RowDragEndEvent) {
      const fromIndex = event.node.rowIndex == null ? -1 : event.node.rowIndex;
      const toIndex = event.overIndex;
      const rowNeedsToMove =
        fromIndex !== toIndex && fromIndex !== -1 && toIndex !== -1;

      if (rowNeedsToMove) {
        if (fromIndex > toIndex) {
          const numberOfMoves = fromIndex - toIndex;
          for (var i = 0; i < numberOfMoves; i++) {
            this.moveUp?.(this.control.path, fromIndex - i)();
          }
        } else {
          const numberOfMoves = toIndex - fromIndex;
          for (var i = 0; i < numberOfMoves; i++) {
            this.moveDown?.(this.control.path, fromIndex + i)();
          }
        }

        this.gridApi?.clearFocusedCell();
      }
    },
    onGridReady(event: GridReadyEvent) {
      this.gridApi = event.api;
      this.gridColumnApi = event.columnApi;
    },
    onSelectionChanged(event: SelectionChangedEvent) {
      const indexes = event.api
        ?.getSelectedNodes()
        .map((node) => this.rowData.findIndex((d) => d[_getRowId] == node.id))
        .filter((v) => v !== -1);

      this.selectedIndexes = indexes ?? [];
    },
  },
});

export default controlRenderer;

const isArray = schemaMatches((schema, _rootSchema) =>
  hasType(schema, 'array')
);

export const entry: JsonFormsRendererRegistryEntry = {
  renderer: controlRenderer,
  tester: rankWith(
    4,
    and(uiTypeIs('Control'), isArray, optionIs('variant', 'ag-grid'))
  ),
};
</script>

<style lang="scss">
@import '~vuetify/src/styles/settings/_variables';

@import '~ag-grid-community/src/styles/ag-grid';
@import '~ag-grid-community/src/styles/ag-theme-alpine/sass/_ag-theme-alpine-mixin';
@import '~ag-grid-community/src/styles/ag-theme-alpine-dark/sass/_ag-theme-alpine-dark-mixin';

// set vuetify default font
$ag-theme-alpine-params: (
  font-family: $body-font-family,

  alpine-active-color: var(--v-primary-base, #1976d2),
  range-selection-border-color: var(--v-primary-base, #1976d2),
  invalid-color: var(--v-error-base, #FF5252),
);
// set vuetify default font
$ag-theme-alpine-dark-params: (
  font-family: $body-font-family,

  alpine-active-color: var(--v-primary-base, #2196F3),
  range-selection-border-color: var(--v-primary-base, #2196F3),
  invalid-color: var(--v-error-base, #FF5252),
);

.ag-grid-renderer {
  @include ag-theme-alpine($ag-theme-alpine-params);
}
.ag-grid-renderer-dark {
  @include ag-theme-alpine-dark($ag-theme-alpine-dark-params);
}

</style>
